import { useMemo } from "react";
import { instantMeiliSearch } from "@meilisearch/instant-meilisearch";
import {
  InstantSearch,
  SearchBox,
  Hits,
  Pagination,
  Stats,
  Configure,
  Highlight,
  SortBy,
  useRefinementList,
} from "react-instantsearch";
import "instantsearch.css/themes/satellite-min.css";
import { InstantSearchInputIcon, InstantSearchResultsLoading } from "@/components/SearchLoading";
import NewBadge from "@/components/NewBadge";
import { useBrandLogos, brandLogoFor } from "@/lib/brandLogos";
import EcuBrandLogo, { ecuBrandLogoAvailable } from "@/components/EcuBrandLogo";
import BoschLookup from "@/components/BoschLookup";

function formatBytes(n) {
  if (!n && n !== 0) return "—";
  const units = ["B", "kB", "MB", "GB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(1)} ${units[i]}`;
}

/**
 * Meilisearch InstantSearch UI for the `toolscraper_dynoecu` index — a
 * fundamentally different schema from the other 4 providers (one row = one
 * original ECU dump file, not a vehicle-to-ECU compatibility mapping), so
 * kept as its own component rather than generalizing the others.
 */
export default function DynoecuInstantSearch({
  meiliHost,
  meiliSearchKey = "unused",
  indexName = "toolscraper_dynoecu",
  hitsPerPage = 20,
  className = "",
}) {
  if (!meiliHost) {
    throw new Error("DynoecuInstantSearch: meiliHost prop is required");
  }

  const brandLogos = useBrandLogos();

  const searchClient = useMemo(
    () => instantMeiliSearch(meiliHost, meiliSearchKey).searchClient,
    [meiliHost, meiliSearchKey],
  );

  return (
    <div className={`text-neutral-100 ${className}`}>
      <InstantSearch searchClient={searchClient} indexName={indexName}>
        <Configure
          hitsPerPage={hitsPerPage}
          attributesToHighlight={["brand", "model", "engine", "hardware", "ecu_type"]}
        />

        <div className="rounded-xl border border-neutral-800 bg-panel p-4">
          <div className="relative flex-1 min-w-[240px]">
            <InstantSearchInputIcon />
            <SearchBox
              placeholder="Suche Marke, Modell, Hardware-/Software-Nr., ECU-Typ…"
              classNames={{
                root: "w-full",
                form: "relative",
                input:
                  "appearance-none [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden flex h-9 w-full rounded-md border border-neutral-800 bg-panel px-3 py-1 pl-9 text-base shadow-sm transition-colors placeholder:text-neutral-500 focus:outline-none focus:ring-1 focus:ring-red-500 md:text-sm text-neutral-100",
                submitIcon: "hidden",
                resetIcon: "hidden",
                loadingIndicator: "hidden",
              }}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6 mt-6">
          <aside className="space-y-5">
            <RefinementFacet
              title="Marke"
              attribute="brand"
              sortBy={["name:asc"]}
              renderIcon={(name) => renderBrandFilterLogo(brandLogos, name)}
            />
            <RefinementFacet
              title="ECU-Hersteller"
              attribute="ecu_maker"
              renderIcon={renderEcuBrandFilterLogo}
            />
          </aside>

          <div className="space-y-4 min-w-0">
            <div className="flex items-center justify-between gap-4">
              <div className="text-xs text-neutral-500">
                <Stats
                  classNames={{ root: "inline" }}
                  translations={{
                    rootElementText: ({ nbHits, processingTimeMS }) =>
                      `${nbHits.toLocaleString("de-DE")} Treffer in ${processingTimeMS}ms`,
                  }}
                />
              </div>
              <SortBy
                items={[
                  { value: indexName, label: "Relevanz" },
                  { value: `${indexName}:brand:asc`, label: "Marke (A-Z)" },
                  { value: `${indexName}:brand:desc`, label: "Marke (Z-A)" },
                ]}
                classNames={{
                  root: "shrink-0",
                  select:
                    "h-9 rounded-md border border-neutral-800 bg-panel text-neutral-100 text-sm px-2.5 focus:outline-none focus:ring-2 focus:ring-[#D32222]",
                }}
              />
            </div>

            <InstantSearchResultsLoading>
              <Hits
                classNames={{
                  root: "",
                  list: "divide-y divide-neutral-800/70 border border-neutral-800/70 rounded-xl overflow-hidden bg-panel",
                  item: "",
                }}
                hitComponent={(props) => <Hit {...props} brandLogos={brandLogos} />}
              />
            </InstantSearchResultsLoading>

            <Pagination
              classNames={{
                root: "flex justify-center pt-2",
                list: "flex items-center gap-1",
                item: "px-3 py-1.5 rounded-md text-sm border border-neutral-800 bg-panel text-neutral-300 hover:text-neutral-50 hover:border-neutral-700 transition",
                selectedItem: "!border-red-500/60 !bg-red-500/15 !text-red-400",
                disabledItem: "opacity-40 pointer-events-none",
                link: "block",
              }}
            />
          </div>
        </div>
      </InstantSearch>
    </div>
  );
}

function RefinementFacet({ title, attribute, renderIcon, sortBy = ["count:desc"] }) {
  const { items, refine, canToggleShowMore, isShowingMore, toggleShowMore } =
    useRefinementList({
      attribute,
      limit: 8,
      showMore: true,
      showMoreLimit: 40,
      sortBy,
    });

  if (items.length === 0) return null;

  return (
    <div className="rounded-xl border border-neutral-800 bg-panel p-4">
      <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-3">
        {title}
      </div>
      <ul className="space-y-1.5">
        {items.map((item) => (
          <li key={item.label} className="flex items-center">
            <label className="flex items-center gap-2 text-sm text-neutral-300 hover:text-neutral-100 cursor-pointer w-full">
              <input
                type="checkbox"
                checked={item.isRefined}
                onChange={() => refine(item.value)}
                className="accent-red-500 w-3.5 h-3.5 rounded border-neutral-700 bg-panel"
              />
              {renderIcon?.(item.label)}
              <span className="flex-1 truncate">{item.label}</span>
              <span className="text-[10px] font-mono text-neutral-500 bg-panel border border-neutral-800 rounded px-1.5 py-0.5">
                {item.count}
              </span>
            </label>
          </li>
        ))}
      </ul>
      {canToggleShowMore && (
        <button
          type="button"
          onClick={toggleShowMore}
          className="mt-2 text-xs text-red-400 hover:text-red-300 transition"
        >
          {isShowingMore ? "Weniger" : "Mehr"}
        </button>
      )}
    </div>
  );
}

function renderEcuBrandFilterLogo(name) {
  return (
    ecuBrandLogoAvailable(name) && (
      <span className="w-7 h-4 flex items-center justify-center shrink-0">
        <EcuBrandLogo name={name} className="max-h-4 max-w-7 w-auto" />
      </span>
    )
  );
}

function renderBrandFilterLogo(brandLogos, name) {
  const logo = brandLogoFor(brandLogos, name);
  return (
    logo && (
      <span className="w-7 h-7 flex items-center justify-center shrink-0">
        <img src={logo} alt="" className="max-h-6 max-w-6 object-contain" loading="lazy" />
      </span>
    )
  );
}

const highlightClassNames = {
  highlighted: "bg-red-500/30 text-red-400 rounded-sm px-0.5",
};

function Hit({ hit, brandLogos }) {
  const logo = brandLogoFor(brandLogos, hit.brand);

  return (
    <div className="group grid grid-cols-1 md:grid-cols-12 gap-4 px-4 py-4 w-full transition">
      {/* Marke / Modell */}
      <div className="md:col-span-3 flex items-center gap-3">
        {logo && (
          <img
            src={logo}
            alt={hit.brand}
            className="h-12 w-12 object-contain rounded bg-neutral-900 border border-neutral-800 p-1 shrink-0"
            loading="lazy"
          />
        )}
        <div>
          <div className="font-medium text-neutral-100">
            <Highlight attribute="brand" hit={hit} classNames={highlightClassNames} />
          </div>
          <div className="text-xs text-neutral-500">{hit.model || "—"}</div>
        </div>
      </div>
      {/* Motor */}
      <div className="md:col-span-2 text-neutral-200">
        {hit.engine ? (
          <Highlight attribute="engine" hit={hit} classNames={highlightClassNames} />
        ) : (
          "—"
        )}
        <NewBadge firstSeen={hit.first_seen} className="mt-1" />
      </div>
      {/* Hardware / Software */}
      <div className="md:col-span-2">
        <div
          className="flex items-center gap-1.5 text-neutral-100"
          style={{ fontFamily: "'JetBrains Mono', monospace" }}
        >
          {hit.hardware ? (
            <Highlight attribute="hardware" hit={hit} classNames={highlightClassNames} />
          ) : (
            "—"
          )}
          <BoschLookup hardware={hit.hardware} />
        </div>
        <div className="text-[11px] text-neutral-500 mt-0.5">
          {hit.software ? `SW ${hit.software}` : ""}
        </div>
      </div>
      {/* ECU */}
      <div className="md:col-span-2">
        <div className="text-neutral-100">
          {hit.ecu_type ? (
            <Highlight attribute="ecu_type" hit={hit} classNames={highlightClassNames} />
          ) : (
            "—"
          )}
        </div>
        <div className="text-[11px] text-neutral-400 mt-0.5">{hit.ecu_maker || ""}</div>
      </div>
      {/* Größe */}
      <div className="md:col-span-1 text-neutral-300 text-sm">{formatBytes(hit.size)}</div>
      {/* Auslesbar mit */}
      <div className="md:col-span-2 flex flex-wrap gap-1.5 items-start">
        {hit.read_hardware ? (
          <span className="inline-flex border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-[10px] rounded px-2 py-0.5">
            {hit.read_hardware}
          </span>
        ) : (
          <span className="text-neutral-600 text-sm">—</span>
        )}
      </div>
    </div>
  );
}
